import type { CrudFilter } from '@refinedev/core'

export const UUID_RE = /^[0-9a-f-]{8,}$/i

const IPV4_OCTET = '(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)'
const IPV4_RE = new RegExp(`^${IPV4_OCTET}(\\.${IPV4_OCTET}){3}$`)

export function isIpAddress(value: string): boolean {
  if (IPV4_RE.test(value)) return true
  if (!value.includes(':') || !/^[0-9a-f:.]+$/i.test(value)) return false
  // The URL parser is the reference IPv6 validator.
  try {
    new URL(`http://[${value}]`)
    return true
  } catch {
    return false
  }
}

/**
 * Build CrudFilters for a search field that can accept a text query or UUID.
 * - If the value looks like a UUID, sets `idField` and clears the text field.
 * - Otherwise sets the text field and clears `idField`.
 *
 * @param value      Search input value
 * @param idField    API field name for ID lookup (e.g. 'ids', 'id')
 * @param opts.asArray   Wrap the ID value in an array (required by v2 endpoints)
 * @param opts.textField Override the text search field name (default: 'name')
 */
export function searchByNameOrId(
  value: string | undefined,
  idField: string,
  opts: boolean | { asArray?: boolean; textField?: string } = false,
): CrudFilter[] {
  const { asArray, textField = 'name' } =
    typeof opts === 'boolean' ? { asArray: opts } : opts
  const isUuid = UUID_RE.test(value ?? '')
  let idValue: unknown
  if (isUuid) {
    idValue = asArray ? [value] : value
  }
  return [
    {
      field: textField,
      operator: 'eq',
      value: isUuid ? undefined : value || undefined,
    },
    { field: idField, operator: 'eq', value: idValue },
  ]
}
